// Must be first: the IAM-CDS MFE uses tsyringe (DI), which needs the
// reflect-metadata polyfill installed on the global Reflect before that bundle
// is imported. Loaded unconditionally so the host works whether or not IAM is on.
import "reflect-metadata";

import {
    ROUTES,
    isCustomerAufgabenRoute,
    mandateWorkspace,
    routeToTarget,
    TREXPERT_AUTH_ENDPOINT,
    TREXPERT_AUTH_NAMESPACE,
    TREXPERT_NO_AUTH_ENDPOINT,
    TREXPERT_NO_AUTH_NAMESPACE,
    TOUCHPOINTS,
    TREXPERT_TRANSPORT_NAME,
} from "@kmu-trexpert/shared";
import { initProjectConfiguration } from "@quatico/magellan-client";
import { MicroFrontendApplication, TouchpointLitElement } from "@qs/app-shell";
import { Capabilities, capabilityConsumer, topic } from "@qs/micro-frontend";

import { DEV_PERSONAS, type DevPersona, findPersona } from "./dev/personas";
import i18n from "./i18n/index";
import {
    applyIamTheme,
    configureIamTransport,
    IAM_FRONTEND,
    TREXPERT_PORTAL_APP_ID,
    IAM_LOGIN_PATH,
    IAM_TOUCHPOINTS,
    iamMicroFrontend,
    isIamCdsEnabled,
} from "./iam";
import { canAccessIamTouchpoint, currentCustomerMandateId, currentTrexpertSide } from "./iam/roles";
import { clearPersona, getPersona, setPersona, trexpertAuthFetchFn } from "./transport";

// The module name. Duplicated from the client's TREXPERT_FRONTEND_NAME rather than
// imported — this host deliberately depends on neither `client` nor `service-proxies`
// (see docs/architecture.md) — so the two must be changed together.
const FRONTEND = "trexpert-portal";
// The MFE is served by the backend at /cds/trexpert-portal/ui (CDS serving model). In
// a browser, resolve it against the current origin; in non-browser contexts fall
// back to an env-configured server URL.
const resolveOriginUrl = (): string => {
    if (typeof window !== "undefined") {
        return `${window.location.origin}/cds/${FRONTEND}/ui/mfe.js`;
    }
    return `${process.env.PUBLIC_SERVER_URL ?? "http://localhost:3000"}/cds/${FRONTEND}/ui/mfe.js`;
};
const ORIGIN_URL = resolveOriginUrl();
const TOUCHPOINT_TAG = "qs-touchpoint-element";

/** The capability a touchpoint uses to ask this shell to navigate. */
const NAVIGATION_GOTO_TOPIC = "/navigation/goto";
/** The provider appears only once a touchpoint has mounted, so connecting retries briefly. */
const NAVIGATION_CONNECT_ATTEMPTS = 40;
const NAVIGATION_CONNECT_RETRY_MS = 50;

type TouchpointEl = HTMLElement & {
    frontend?: string;
    name?: string;
    properties?: Record<string, unknown>;
    provideCapabilities?: string[];
};

if (!customElements.get(TOUCHPOINT_TAG)) {
    // TouchpointLitElement is a Lit element; in this package's compile its base
    // HTMLElement (from lit) and the DOM lib's HTMLElement are seen as distinct,
    // so define() rejects it. A CustomElementConstructor cast bridges the known
    // Lit/TS friction — it is a real custom element at runtime.
    customElements.define(TOUCHPOINT_TAG, TouchpointLitElement as unknown as CustomElementConstructor);
}

/**
 * Configure the Magellan transport BEFORE the app-shell mounts touchpoints:
 * both namespaces use the persona-token transport (decision D2). Runs in the
 * app-shell `beforeAll` hook.
 */
const configureTransport = (): void => {
    initProjectConfiguration({
        namespaces: {
            [TREXPERT_NO_AUTH_NAMESPACE]: {
                transport: TREXPERT_TRANSPORT_NAME,
                endpoint: TREXPERT_NO_AUTH_ENDPOINT,
            },
            [TREXPERT_AUTH_NAMESPACE]: {
                transport: TREXPERT_TRANSPORT_NAME,
                endpoint: TREXPERT_AUTH_ENDPOINT,
            },
        },
        transports: { [TREXPERT_TRANSPORT_NAME]: trexpertAuthFetchFn },
    });
    // Register the IAM-CDS namespaces + transport too, when delegation is on.
    configureIamTransport();
};

const host = (): HTMLElement | null => document.querySelector("[data-testid='touchpoint-host']");

const mountTouchpoint = (
    name: string,
    properties: Record<string, unknown> = {},
    frontend: string = FRONTEND
): void => {
    const container = host();
    if (!container) {
        return;
    }
    const element = document.createElement(TOUCHPOINT_TAG) as TouchpointEl;
    element.frontend = frontend;
    element.name = name;
    element.properties = properties;
    // This shell IS a host: it accepts the touchpoint's navigation requests (see consumeNavigation).
    // Declaring it here is what BINDS the provider the touchpoint's manifest offers — without it the
    // channel stays unbound and the app-shell logs "Could not bind capability provider" per mount.
    //
    // OUR frontend only. The app-shell logs the mirror-image error when an element declares a
    // provider the micro-frontend does not offer, and iam-cds offers no navigation capability — so
    // declaring it for every mount would just trade one error per trexpert touchpoint for one per
    // IAM touchpoint.
    if (frontend === FRONTEND) {
        element.provideCapabilities = [NAVIGATION_GOTO_TOPIC];
    }
    container.replaceChildren(element);
    if (frontend === FRONTEND) {
        consumeNavigation();
    }
};

/**
 * Accept `/navigation/goto` from whichever touchpoint is mounted, and navigate by setting the hash.
 *
 * The touchpoint sends the destination it resolved plus the canonical `hash` for it; we use the
 * hash, because this shell's router already turns a hash into the right touchpoint and — unlike
 * mounting directly — it leaves a URL the user can bookmark, share and go Back to.
 *
 * The provider only exists once a touchpoint has mounted, so this retries until the channel is
 * there, then stops. Re-entrant by design: mounting swaps the element, so the connection is remade
 * against the new one.
 */
const consumeNavigation = (): void => {
    const gotoTopic = topic(NAVIGATION_GOTO_TOPIC);
    const messaging = MicroFrontendApplication.getMessaging();
    const connect = (attempt = 0): void => {
        const consumer = new Capabilities([], [capabilityConsumer(gotoTopic)], messaging).getConsumer<
            { hash?: string },
            void
        >(gotoTopic);
        if (consumer) {
            consumer(request => {
                if (request.hash) {
                    window.location.hash = request.hash;
                }
            });
            return;
        }
        if (attempt < NAVIGATION_CONNECT_ATTEMPTS) {
            window.setTimeout(() => connect(attempt + 1), NAVIGATION_CONNECT_RETRY_MS);
        }
    };
    connect();
};

/**
 * Mount the IAM `login-button` into the footer slot (where the persona switcher
 * lives in in-memory mode). It renders the signed-in user + logout when
 * authenticated; with `autoRedirect` it sends an unauthenticated visitor to the
 * login page (`loginUrl`, with `?redirect_uri=` back to here). The login page
 * itself does NOT mount this button, so there is no redirect loop.
 */
const mountLoginButton = (): void => {
    const footer = document.querySelector("[data-testid='persona-footer']");
    if (!footer) {
        return;
    }
    const button = document.createElement(TOUCHPOINT_TAG) as TouchpointEl;
    button.frontend = IAM_FRONTEND;
    button.name = IAM_TOUCHPOINTS.loginButton;
    button.properties = {
        appId: TREXPERT_PORTAL_APP_ID,
        autoRedirect: true,
        loginUrl: IAM_LOGIN_PATH,
        showUserName: true,
    };
    footer.replaceChildren(button);
};

const showLoginPrompt = (): void => {
    // In IAM mode the footer's login-button (autoRedirect) handles an
    // unauthenticated visitor by sending them to the login page, so there is
    // nothing to render in the content area here.
    if (isIamCdsEnabled()) {
        return;
    }
    const container = host();
    if (!container) {
        return;
    }
    // Light DOM: Tailwind utilities are not available here, so style inline.
    const prompt = document.createElement("p");
    prompt.dataset.testid = "login-prompt";
    prompt.style.padding = "2rem";
    prompt.style.color = "#4e5561";
    prompt.textContent = i18n.t("loginPrompt");
    container.replaceChildren(prompt);
};

interface NavItem {
    label: string;
    href: string;
    isActive: (hash: string) => boolean;
}

/**
 * IAM-CDS administration nav, shown when delegation is on. Each entry routes to
 * an IAM management touchpoint (served by the IAM MFE, mounted in
 * `renderTouchpoint`). The hash is the source of truth for both nav highlight
 * and which touchpoint mounts, so the two stay in lockstep.
 *
 * `aliases` carries the hashes the IAM MFE's own router canonicalises to. Two of
 * its touchpoints rewrite the hash after mounting — `#/applications` → `#/apps`,
 * `#/identity-providers` → `#/idp` — so the route we navigate to is not the route
 * we end up on. Without the alias the rewritten hash matches no entry, and the
 * `visible[0]` guard in {@link renderTouchpoint} treats it as unknown and bounces
 * to the first nav item; a bookmark on `#/apps` then lands on Benutzerverwaltung.
 * We keep the long form as `hash` (it is what the nav links to and what reads
 * well in a URL) and accept the MFE's short form alongside it.
 */
const IAM_NAV: ReadonlyArray<{
    hash: string;
    labelKey: string;
    touchpoint: string;
    aliases?: ReadonlyArray<string>;
}> = [
    { hash: "#/users", labelKey: "navigation:iam.users", touchpoint: IAM_TOUCHPOINTS.userManagement },
    {
        hash: "#/organizations",
        labelKey: "navigation:iam.organizations",
        touchpoint: IAM_TOUCHPOINTS.organizationManagement,
    },
    {
        hash: "#/templates",
        labelKey: "navigation:iam.templates",
        touchpoint: IAM_TOUCHPOINTS.templateManagement,
    },
    {
        hash: "#/applications",
        labelKey: "navigation:iam.applications",
        touchpoint: IAM_TOUCHPOINTS.applicationManagement,
        aliases: ["#/apps"],
    },
    {
        hash: "#/identity-providers",
        labelKey: "navigation:iam.identityProviders",
        touchpoint: IAM_TOUCHPOINTS.identityProviderManagement,
        aliases: ["#/idp"],
    },
];

/** Whether `hash` addresses this IAM entry — its own hash or an MFE-canonical alias. */
const matchesIamEntry = (entry: (typeof IAM_NAV)[number], hash: string): boolean =>
    hash === entry.hash || (entry.aliases?.includes(hash) ?? false);

/** IAM admin nav items the signed-in user may access (role-gated per touchpoint). */
const iamNavItems = (): NavItem[] =>
    IAM_NAV.filter(entry => canAccessIamTouchpoint(entry.touchpoint)).map(entry => ({
        label: i18n.t(entry.labelKey),
        href: entry.hash,
        isActive: hash => matchesIamEntry(entry, hash),
    }));

/** The Profil entry: the IAM `profile-view` touchpoint (account self-service). */
const PROFILE_HASH = "#/profile";
const profileNavItem = (): NavItem => ({
    label: i18n.t("navigation:profile"),
    href: PROFILE_HASH,
    isActive: hash => hash === PROFILE_HASH,
});

/**
 * Sidebar nav for the signed-in persona's role (mirrors the oracle's Sidebar).
 * Only routes that resolve to a real touchpoint are listed — the oracle's
 * Dashboard/Aufgaben/Einstellungen/Team pages do not exist here yet.
 */
const navItemsFor = (persona: DevPersona): NavItem[] =>
    persona.role === "customer" ? customerNavItems() : trusteeNavItems();

/**
 * The customer nav: Dashboard, Dateien, Aufgaben, Posteingang, Einstellungen.
 * «Dokument hochladen» is no longer a peer nav item — it is the customer's
 * primary task, so it renders as a set-off primary button above this list
 * (see {@link renderUploadAction}). Used for the customer dev persona and as the
 * customer-side app nav in IAM mode. One mandate per customer, so
 * Dateien/Aufgaben/Belege are flat top-level routes resolved against the caller's
 * single mandate server-side — no mandate ID in the URL (Epic A, slices 1+2).
 */
const customerNavItems = (): NavItem[] => [
    {
        label: i18n.t("navigation:dashboard"),
        href: ROUTES.dashboard,
        isActive: hash => hash === ROUTES.dashboard,
    },
    {
        label: i18n.t("navigation:documents"),
        href: ROUTES.dokumente,
        isActive: hash => hash === ROUTES.dokumente || /^#\/dokumente\/[^/]+$/.test(hash),
    },
    {
        label: i18n.t("navigation:tasks"),
        href: ROUTES.aufgaben,
        isActive: hash => isCustomerAufgabenRoute(hash),
    },
    // «Posteingang» is hidden from the customer view (decision Michael, 2026-07-21):
    // the customer cannot write messages and every relevant item already lives under
    // «Dokumente» and «Aufgaben». The i18n label and the touchpoint stay in the code
    // so the customer inbox is reactivatable; the trustee inbox is unaffected.
    {
        label: i18n.t("navigation:forms"),
        href: ROUTES.formulare,
        isActive: hash => hash === ROUTES.formulare,
    },
    {
        label: i18n.t("navigation:settings"),
        href: ROUTES.settings,
        isActive: hash => hash === ROUTES.settings,
    },
];

/**
 * The Trexpert-team (trustee) nav: Dashboard, Mandate, Dateien, Aufgaben,
 * Posteingang, Einstellungen. Used for the trustee dev persona and as the
 * trexpert app nav in IAM mode (where there is no dev persona).
 */
const trusteeNavItems = (): NavItem[] => [
    {
        label: i18n.t("navigation:dashboard"),
        href: ROUTES.dashboard,
        isActive: hash => hash === ROUTES.dashboard,
    },
    {
        label: i18n.t("navigation:mandates"),
        href: ROUTES.mandates,
        isActive: hash => hash.startsWith(ROUTES.mandates),
    },
    {
        label: i18n.t("navigation:documents"),
        href: ROUTES.dokumente,
        isActive: hash => hash.startsWith(ROUTES.dokumente),
    },
    { label: i18n.t("navigation:tasks"), href: ROUTES.aufgaben, isActive: hash => hash === ROUTES.aufgaben },
    {
        label: i18n.t("navigation:inbox"),
        href: ROUTES.inbox,
        isActive: hash => hash === ROUTES.inbox,
    },
    {
        label: i18n.t("navigation:settings"),
        href: ROUTES.settings,
        isActive: hash => hash === ROUTES.settings,
    },
];

/**
 * Trexpert app nav for IAM mode, by the user's side: a trustee sees the
 * trustee nav (Mandate, Posteingang, …), a customer sees the customer nav
 * (their own Dateien/Aufgaben/Belege). This matches the AuthorizationGuard —
 * a customer is never offered trustee-only items that would 403 («Kein Zugriff»).
 * With no trexpert-portal role the user sees no app items — only the IAM admin
 * items they may access + Profil.
 */
const trexpertNavItemsForIam = (): NavItem[] => {
    switch (currentTrexpertSide()) {
        case "trexpert":
            return trusteeNavItems();
        case "customer":
            return customerNavItems();
        default:
            return [];
    }
};

/**
 * The full IAM-mode nav (in display order): the role-filtered trexpert app nav,
 * then the accessible IAM-CDS administration items, then Profil. Shared by
 * `renderNav` (what to render) and `renderTouchpoint` (where to land by default).
 */
const iamModeNavItems = (): NavItem[] => [...trexpertNavItemsForIam(), ...iamNavItems(), profileNavItem()];

/**
 * Whether the signed-in user acts on the customer side — a dev customer persona,
 * or (IAM mode) a user whose trexpert-portal role is customer-side. Drives the
 * customer-only «Dokument hochladen» primary button.
 */
const isCustomerView = (): boolean => {
    if (isIamCdsEnabled()) {
        return currentTrexpertSide() === "customer";
    }
    return findPersona(getPersona())?.role === "customer";
};

/**
 * Render the customer's «Dokument hochladen» primary button into the set-off slot
 * above the nav list (desktop sidebar + mobile drawer). Shown only on the
 * customer side; cleared and hidden for trustees and the unauthenticated state.
 */
const renderUploadAction = (): void => {
    const slots = document.querySelectorAll<HTMLElement>(
        "[data-testid='sidebar-upload-action'], [data-testid='mobile-upload-action']"
    );
    const show = isCustomerView();
    const hash = window.location.hash;
    for (const slot of slots) {
        if (!show) {
            slot.replaceChildren();
            slot.classList.remove("is-shown");
            continue;
        }
        const link = document.createElement("a");
        link.href = ROUTES.hochladen;
        link.dataset.testid = "upload-action-button";
        link.className = "upload-action__button";
        link.textContent = i18n.t("navigation:uploadReceipts");
        if (hash === ROUTES.hochladen) {
            link.setAttribute("aria-current", "page");
        }
        slot.replaceChildren(link);
        slot.classList.add("is-shown");
    }
};

/** Render the role-based nav into both the desktop sidebar and the mobile drawer. */
const renderNav = (): void => {
    // IAM mode: identity comes from the IAM session, not a dev persona; show the
    // role-filtered trexpert app nav + the IAM-CDS administration nav (Benutzer,
    // Organisationen, Vorlagen, …) the user may access + Profil.
    const persona = findPersona(getPersona());
    const items = isIamCdsEnabled() ? iamModeNavItems() : persona ? navItemsFor(persona) : [];
    const hash = window.location.hash;
    const containers = document.querySelectorAll<HTMLElement>(
        "[data-testid='sidebar-nav'], [data-testid='mobile-nav']"
    );
    for (const container of containers) {
        const links = items.map(item => {
            const link = document.createElement("a");
            link.href = item.href;
            link.dataset.testid = "nav-link";
            const active = item.isActive(hash);
            link.className = active ? "nav-link nav-link--active" : "nav-link";
            if (active) {
                link.setAttribute("aria-current", "page");
            }
            link.textContent = item.label;
            return link;
        });
        container.replaceChildren(...links);
    }
    renderUploadAction();
};

/**
 * Wire the mobile drawer (open/close/backdrop). The persona footer is a single
 * instance: it lives in the sidebar on desktop and moves into the drawer while
 * the drawer is open, so its `<select>` (and its onchange) is never duplicated.
 */
const setupMobileMenu = (): void => {
    const toggle = document.querySelector<HTMLButtonElement>("[data-testid='mobile-menu-toggle']");
    const close = document.querySelector<HTMLButtonElement>("[data-testid='mobile-menu-close']");
    const backdrop = document.querySelector<HTMLElement>("[data-testid='mobile-menu-backdrop']");
    const drawer = document.querySelector<HTMLElement>("[data-testid='mobile-menu']");
    const drawerFooter = document.querySelector<HTMLElement>("#drawer-footer");
    const sidebarFooter = document.querySelector<HTMLElement>("#sidebar-footer");
    const personaFooter = document.querySelector<HTMLElement>("[data-testid='persona-footer']");

    const openMenu = (): void => {
        drawer?.classList.add("is-open");
        backdrop?.classList.add("is-open");
        toggle?.setAttribute("aria-expanded", "true");
        if (drawerFooter && personaFooter) {
            // Both footers hold nothing but the persona controls, so this is an
            // append in practice — `insertBefore(node, null)` when empty.
            drawerFooter.insertBefore(personaFooter, drawerFooter.firstChild);
        }
    };
    const closeMenu = (): void => {
        drawer?.classList.remove("is-open");
        backdrop?.classList.remove("is-open");
        toggle?.setAttribute("aria-expanded", "false");
        if (sidebarFooter && personaFooter) {
            sidebarFooter.insertBefore(personaFooter, sidebarFooter.firstChild);
        }
    };

    toggle?.addEventListener("click", openMenu);
    close?.addEventListener("click", closeMenu);
    backdrop?.addEventListener("click", closeMenu);
    // A nav-link or the upload-action button click navigates; close the drawer behind it.
    //
    // Deferred to a macrotask, deliberately. Closing moves `personaFooter` between
    // the two footers, and `renderNav` replaces both nav containers' children — DOM
    // mutations that, run inside the click tick, land while the embedded IAM-CDS
    // touchpoint is being torn down and remounted by the `hashchange` that this same
    // click triggers. The outgoing IAM MFE reacts by restoring its own route
    // (`history.replaceState` from `cds/iam/ui/mfe.js`), silently reverting the hash
    // to the previously mounted touchpoint — so every drawer tap bounced back to
    // whichever IAM entry was open. Letting the click, the hashchange, and the
    // remount finish first keeps the teardown clear of that sequence.
    // Desktop is unaffected: its sidebar neither closes nor moves the footer.
    drawer?.addEventListener("click", event => {
        const target = event.target as HTMLElement;
        if (
            target.closest("[data-testid='nav-link']") ||
            target.closest("[data-testid='upload-action-button']")
        ) {
            setTimeout(closeMenu, 0);
        }
    });
};

/**
 * Route the mounted touchpoint by hash, then persona role (decisions D1, D14).
 * An explicit hash is honoured for any persona — the AuthorizationGuard decides
 * access, so a customer deep-linking another org's Mandate, or the full list,
 * lands on a touchpoint that renders «Kein Zugriff» rather than data (D8).
 */
const renderTouchpoint = (): void => {
    // IAM mode: identity comes from the IAM session, not a dev persona. The
    // login-button (autoRedirect) sends unauthenticated visitors to the login
    // page, so reaching here means signed in. Route the IAM-CDS administration
    // touchpoints + Profil to the IAM MFE; the trexpert app routes (Dashboard,
    // Mandate, Dateien, …) fall through to the shared trustee routing.
    if (isIamCdsEnabled()) {
        // Mount only what the user may see. If the selected hash isn't a visible
        // menu item — an unknown hash, or one gated away by role (e.g. a
        // non-admin deep-linking #/settings or #/identity-providers) — redirect
        // to the first visible item rather than mounting a forbidden touchpoint.
        const visible = iamModeNavItems();
        const selected = visible.find(item => item.isActive(window.location.hash));
        // «Dokument hochladen» (#/hochladen) is deliberately not a nav item — it is
        // the customer's set-off primary button (see {@link renderUploadAction}) —
        // so it never matches a visible item. Without this exception the guard below
        // would redirect it to the landing item (Dashboard), and the upload view
        // would be unreachable from any view. It falls through to
        // renderCustomerRoute, which mounts the upload touchpoint.
        const isCustomerUploadRoute = isCustomerView() && window.location.hash === ROUTES.hochladen;
        if (!selected && !isCustomerUploadRoute) {
            const landing = visible[0];
            if (landing) {
                window.location.hash = landing.href;
                return; // the hashchange handler re-runs renderNav + renderTouchpoint
            }
            // No visible items at all (no roles) — nothing to mount.
            return;
        }
        const iamRoute = IAM_NAV.find(entry => matchesIamEntry(entry, window.location.hash));
        if (iamRoute) {
            mountTouchpoint(iamRoute.touchpoint, {}, IAM_FRONTEND);
            return;
        }
        if (window.location.hash === PROFILE_HASH) {
            mountTouchpoint(IAM_TOUCHPOINTS.profile, {}, IAM_FRONTEND);
            return;
        }
        // Customer in IAM mode: route their flat Dateien/Aufgaben/Belege against
        // their own Mandate (id = org id, taken from the token role). Otherwise
        // fall through to the trustee routes (Dashboard, Mandate, …).
        const iamCustomerMandateId = currentCustomerMandateId();
        if (iamCustomerMandateId && renderCustomerRoute(iamCustomerMandateId)) {
            return;
        }
        renderTrusteeRoute();
        return;
    }
    const persona = findPersona(getPersona());
    if (!persona) {
        showLoginPrompt();
        return;
    }
    // Customer-flat routes (Epic A): gated on the customer role so they never
    // shadow the trustee's mandate-scoped routes; a trustee hitting them falls
    // through to the default landing.
    if (persona.role === "customer" && renderCustomerRoute(persona.mandateId ?? "")) {
        return;
    }

    renderTrusteeRoute(persona.mandateId ?? "");
};

/**
 * Customer-flat routing (Epic A): one Mandate per customer, so Dateien, Aufgaben
 * and Belege hochladen resolve against that single `mandateId` without an id in
 * the URL. Returns `true` when it handled the current hash, `false` otherwise
 * (so the caller falls through). Shared by the customer dev persona and IAM mode.
 */
const renderCustomerRoute = (mandateId: string): boolean => {
    const hash = window.location.hash;
    if (hash === ROUTES.inbox) {
        // The customer inbox is hidden (see customerNavItems). Redirect a typed or
        // bookmarked #/inbox to the dashboard instead of falling through to the
        // trustee inbox route. The hashchange handler re-runs renderNav + touchpoint.
        // A redirect is the shell's business, so it stays here rather than in the shared
        // table, which answers "where does this route lead", not "fix the URL".
        window.location.hash = ROUTES.dashboard;
        return true;
    }
    const target = routeToTarget(hash, { side: "customer", mandateId });
    if (!target) {
        return false;
    }
    mountTouchpoint(target.touchpoint, target.properties ?? {});
    return true;
};

/**
 * The Trexpert-team (trustee) hash routing: Mandate-scoped routes plus the flat
 * Dashboard / Dokumente / Aufgaben / Posteingang / Einstellungen. Shared by the
 * trustee dev persona and IAM mode (which has no persona — `settingsMandateId`
 * is then ""). TaskLists use the editor touchpoint (trustee), and an explicit
 * hash is honoured; the AuthorizationGuard decides access (D8).
 */
const renderTrusteeRoute = (settingsMandateId = ""): void => {
    const hash = window.location.hash;
    // Category management moved into the Dokumente view; the standalone settings route is
    // gone. Redirect a bookmarked deep link so the URL is corrected — the shared table
    // reports the same destination, but only the shell can rewrite the address bar.
    const mandateSettingsMatch = /^#\/mandates\/([^/]+)\/settings$/.exec(hash);
    if (mandateSettingsMatch?.[1]) {
        window.location.hash = mandateWorkspace(mandateSettingsMatch[1]);
        return;
    }
    const target = routeToTarget(hash, {
        side: "trexpert",
        ...(settingsMandateId ? { mandateId: settingsMandateId } : {}),
    });
    // Default landing: dashboard (wiring phase, impl-dashboard). An unrecognised URL is the
    // shell's call, which is why the table reports "not mine" rather than guessing.
    mountTouchpoint(target?.touchpoint ?? TOUCHPOINTS.dashboard, target?.properties ?? {});
};

const updateSignOutVisibility = (): void => {
    const signOut = document.querySelector<HTMLButtonElement>("[data-testid='sign-out']");
    if (signOut) {
        signOut.hidden = getPersona() === null;
    }
};

/** Fine German role label for a persona, shown in the persona switcher. */
const personaRoleLabel = (persona: DevPersona): string =>
    i18n.t(`navigation:personaRole.${persona.personaRole}`);

const personaInitials = (name: string): string =>
    name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(word => word[0]?.toUpperCase() ?? "")
        .join("") || "?";

const syncPortalLabel = (): void => {
    const persona = findPersona(getPersona());
    const base = persona
        ? i18n.t(`navigation:portalLabel.${persona.role}`)
        : i18n.t("navigation:portalLabel.customer");
    // Customers see «Kundenportal» over the company name on a second line; the
    // trustee just «Kundenportal» (FB v0.1.104). The company name wraps onto its
    // own line so a long name never crowds the «Kundenportal» label.
    const companyName = persona?.role === "customer" ? persona.companyName : undefined;
    for (const el of document.querySelectorAll<HTMLElement>("[data-portal-label]")) {
        el.replaceChildren();
        if (companyName) {
            const lead = document.createElement("span");
            lead.className = "portal-label__lead";
            lead.textContent = base;
            const company = document.createElement("span");
            company.className = "portal-label__company";
            company.textContent = companyName;
            el.append(lead, company);
        } else {
            el.textContent = base;
        }
    }
};

/**
 * Sync the styled persona trigger (avatar / name / role) with the active
 * persona. The native <select> stays the source of truth and remains the BDD
 * interaction surface; this only reflects state into the visible chrome.
 */
const syncPersonaTrigger = (): void => {
    const persona = findPersona(getPersona());
    const avatar = document.querySelector<HTMLElement>("[data-persona-avatar]");
    const name = document.querySelector<HTMLElement>("[data-persona-name]");
    const role = document.querySelector<HTMLElement>("[data-persona-role]");
    if (avatar) {
        avatar.textContent = persona ? personaInitials(persona.name) : "?";
    }
    if (name) {
        name.textContent = persona ? persona.name : i18n.t("navigation:personaSelectLabel");
    }
    if (role) {
        role.textContent = persona ? personaRoleLabel(persona) : "";
    }
    syncPortalLabel();
};

const renderPersonaSwitcher = (): void => {
    const select = document.querySelector<HTMLSelectElement>("[data-testid='persona-switcher']");
    if (!select) {
        return;
    }
    select.replaceChildren(new Option(i18n.t("navigation:personaSelectPlaceholder"), ""));
    for (const persona of DEV_PERSONAS) {
        select.add(new Option(persona.name, persona.id));
    }
    select.value = getPersona() ?? "";
    select.onchange = () => {
        if (select.value) {
            setPersona(select.value);
            window.location.hash = "";
        } else {
            clearPersona();
        }
        syncPersonaTrigger();
        updateSignOutVisibility();
        renderNav();
        renderTouchpoint();
    };

    const signOut = document.querySelector<HTMLButtonElement>("[data-testid='sign-out']");
    if (signOut) {
        signOut.onclick = () => {
            clearPersona();
            select.value = "";
            syncPersonaTrigger();
            updateSignOutVisibility();
            renderNav();
            renderTouchpoint();
        };
    }
    syncPersonaTrigger();
    updateSignOutVisibility();
};

const start = async (): Promise<void> => {
    // Re-skin the IAM-CDS touchpoints to match the trexpert design system, before
    // any touchpoint mounts. Inert when IAM is off (no touchpoints to affect).
    applyIamTheme();

    const microFrontends = [
        { name: FRONTEND, originUrl: ORIGIN_URL, useTouchpoints: "*" as const },
        iamMicroFrontend(),
    ].filter((mfe): mfe is NonNullable<typeof mfe> => mfe !== null);

    await new MicroFrontendApplication()
        .loadConfiguration({ name: "trexpert-portal", microFrontends })
        .start({ beforeAll: configureTransport });

    // The dev-persona switcher is the in-memory login. With IAM-CDS delegation on,
    // replace it with the IAM `login-button` session control: it shows the signed-in
    // user + logout when authenticated, and (autoRedirect) sends an unauthenticated
    // visitor to the login page. So the app chrome is only ever seen while logged in.
    if (isIamCdsEnabled()) {
        mountLoginButton();
    } else {
        renderPersonaSwitcher();
    }
    setupMobileMenu();
    renderNav();
    renderTouchpoint();
    window.addEventListener("hashchange", () => {
        renderNav();
        renderTouchpoint();
    });
};

void start();
